import React, { useState, useEffect, useRef } from 'react'
import moment from 'moment'
import Yup from '../../../../util/Validacoes'
import { HeaderCadastro } from '../../../../components/Headers'
import { mostrarToast } from '../../../../components/Toasts'
import { Formik, Form, FieldArray } from 'formik'
import { BotaoPadrao } from '../../../../components/Botoes'
import { mensagens } from '../../../../util/Utilitarios'
import { useParams, Link } from 'react-router-dom'
import { salvar, get } from '../../../../util/AcessoApi'
import '../../../../assets/css/icones/iconsmind/style.css';
import Atalho from '../../../../util/Atalho'
import { InputAtendimentoPosAnestesico, InputSexo } from '../../../../components/InputsAutoComplete'
import { InputCheckboxNullable, InputPadrao } from '../../../../components/InputsV2'
import { ModalDuasOpcoes } from '../../../../components/Modais'
import ContentLoader from 'react-content-loader'
import { Perguntas } from '../../consultaPreAnestesico/pages/Cadastro'
import { TIPO_PERGUNTA } from '../../../../util/Enums'
import '../css/cadastro.css'

// Campos do cabeçalho que vêm junto com a guia selecionada. Idade e sexo (sexo_opcao) só pré-preenchem:
// são livres na ficha e gravados nela como registro histórico, sem alterar o cadastro do paciente
const CAMPOS_ATENDIMENTO = ['paciente', 'data_procedimento', 'data_nascimento', 'idade', 'sexo', 'sexo_opcao', 'operadora_saude', 'estabelecimento_saude', 'anestesiologista']

// idade atual pela data de nascimento, como na combo de atendimento
const idadePor = nascimento => nascimento && moment(nascimento, 'DD/MM/YYYY', true).isValid()
    ? moment().diff(moment(nascimento, 'DD/MM/YYYY'), 'years').toString() : ''

// Ao menos uma resposta: opção marcada, "Negativo" marcado ou texto na observação
const possuiResposta = (values) => (values.agrupamentos || [])
    .flatMap(x => x.questoes || [])
    .some(q => q && (q.nao_possui === true || (q.observacao && q.observacao.toString().trim()) || (q.opcoes || []).some(op => op.marcado)))

const MENSAGEM_SEM_RESPOSTA = 'Informe ao menos uma resposta nos indicadores assistenciais.'

const mostrarErros = (e) => {
    if (e && e.response && e.response.data && e.response.data.erros)
        e.response.data.erros.forEach(item => mostrarToast('erro', item))
    else
        mostrarToast('erro', mensagens.erro)
}

// Ficha pós-anestésica. Pelo módulo, grava a ficha de um atendimento; com a prop guia, é o passo 2
// do cadastro da guia: os dados vêm do passo 1 e a ficha vai junto no salvar da guia (guia.salvar).
// Guia: { fichaId, cabecalho, novo, visivel, salvando, salvar(pos, novo) }. Sem resposta a guia é gravada sem
// ficha; a regra do hospital acreditado fica no backend.
// cabecalho: { guia_id, paciente_id, paciente, data_nascimento, operadora_saude, estabelecimento_saude, socio }
const CadastroPosAnestesico = (props) => {
    const { guia } = props
    const params = useParams()
    // no passo 2 o parâmetro da rota é o id da guia, não o da ficha
    const id = guia ? guia.fichaId : params.id

    const [salvando, setSalvando] = useState(false)
    const [carregando, setCarregando] = useState(true)
    const [showModalConfirmarSalvar, setShowModalConfirmarSalvar] = useState(false)
    const [valoresIniciais, setValoresIniciais] = useState({ atendimento: null, agrupamentos: [] })
    const salvarENovo = useRef(false)
    const formikRef = useRef()
    const carregado = useRef(false)
    const pacienteId = guia && guia.cabecalho.paciente_id
    const nascimentoGuia = guia && guia.cabecalho.data_nascimento

    // ficha nova pela guia: idade (pela data do passo 1 ou do cadastro) e sexo do paciente, só como sugestão
    const sugestaoPaciente = () => !pacienteId ? Promise.resolve({ idade: idadePor(nascimentoGuia), sexo_opcao: null })
        : Promise.resolve(get(`/faturamento/beneficiario/${pacienteId}`)).then(b => ({
            idade: idadePor(nascimentoGuia || (b && b.data_nascimento)),
            sexo_opcao: (b && b.sexo) || null
        }))

    // troca de paciente (ou da data) no passo 1 depois de carregada a ficha nova: refaz a sugestão
    useEffect(_ => {
        if (!guia || guia.fichaId || !carregado.current)
            return

        sugestaoPaciente().then(s => {
            formikRef.current && formikRef.current.setFieldValue('idade', s.idade)
            formikRef.current && formikRef.current.setFieldValue('sexo_opcao', s.sexo_opcao)
        }).catch(mostrarErros)
    }, [pacienteId, nascimentoGuia])

    // idade e sexo obrigatórios na ficha; no passo 2, só quando a ficha vai ser gravada (tem resposta)
    const exigePaciente = values => !guia || possuiResposta(values)
    const [validacao] = useState(Yup.object({
        ...(guia ? {} : { atendimento: Yup.object().required().nullable() }),
        idade: Yup.string().nullable()
            .test('idade', 'Campo Obrigatório!', function (valor) { return !exigePaciente(this.parent) || (valor != null && valor.toString().trim() !== '') }),
        sexo_opcao: Yup.object().nullable()
            .test('sexo', 'Campo Obrigatório!', function (valor) { return !exigePaciente(this.parent) || !!(valor && valor.value) })
    }))

    // Em alteração o atendimento fica travado
    const atendimentoTravado = !!id

    useEffect(_ => {
        Promise.resolve(get(`/posanestesico/consultaposanestesico/${id || 0}`)).then(async obj => {
            const valores = { ...obj, idade: obj && obj.idade != null ? obj.idade.toString() : '', agrupamentos: (obj && obj.agrupamentos) || [] }
            const sugestao = guia && !guia.fichaId ? await sugestaoPaciente().catch(_ => ({})) : {}
            carregado.current = true
            setCarregando(false)
            setValoresIniciais({ ...valores, ...sugestao })
        }).catch(e => {
            setCarregando(false)
            mostrarErros(e)
        })
    }, [id])

    const onChangeAtendimento = (propsFormik, option) => {
        CAMPOS_ATENDIMENTO.forEach(campo => propsFormik.setFieldValue(campo, option ? option[campo] : null))
    }

    const Submit = (values, propsFormik) => {
        setShowModalConfirmarSalvar(false)
        setSalvando(true)
        salvar(
            { ...values },
            '/posanestesico/formulario',
            _ => {
                mostrarToast('sucesso', mensagens.sucesso)
                props.history.push('/posanestesico/consultaposanestesico/consulta')
            },
            _ => setSalvando(false))
    }

    const onSubmit = values => {
        if (guia)
            return guia.salvar({ agrupamentos: values.agrupamentos, idade: values.idade, sexo_opcao: values.sexo_opcao }, salvarENovo.current)

        if (!possuiResposta(values))
            return mostrarToast('erro', MENSAGEM_SEM_RESPOSTA)

        setShowModalConfirmarSalvar(true)
    }

    const ocupado = salvando || (guia && guia.salvando)

    return (
        <Formik
            innerRef={formikRef}
            initialValues={valoresIniciais}
            validationSchema={validacao}
            enableReinitialize={true}
            onSubmit={onSubmit}>
            {propsFormik => {
                return (<Form>
                    <div className='card mb-20'>
                        <div className='card-body'>
                            <div className='row'>
                                <InputPadrao label='Id' name='id' type='hidden' placeholder='Id' />
                                {guia ? <>
                                    {/* mesmo cabeçalho da ficha pelo módulo, com os dados do passo 1 */}
                                    <CampoLeitura label='Atendimento (Guia / Paciente)' tamanho='col-6'
                                        valor={[guia.cabecalho.guia_id, guia.cabecalho.paciente].filter(x => x).join(' - ')} />
                                    <InputPadrao label='Idade' name='idade' placeholder='Idade' tamanho='col-3' type='number' obrigatorio />
                                    <InputSexo label='Sexo' name='sexo_opcao' placeholder='Sexo' tamanho='col-3' propsFormik={propsFormik} obrigatorio />
                                    <CampoLeitura label='Operadora Saúde' tamanho='col-6' valor={guia.cabecalho.operadora_saude} />
                                    <CampoLeitura label='Estabelecimento Saúde' tamanho='col-6' valor={guia.cabecalho.estabelecimento_saude} />
                                    {/* dono da ficha: o já gravado nela ou, na ficha nova, o primeiro executante da equipe */}
                                    <CampoLeitura label='Sócio' tamanho='col-6' valor={guia.fichaId ? propsFormik.values.anestesiologista : guia.cabecalho.socio} />
                                </> : <>
                                    <InputAtendimentoPosAnestesico
                                        label='Atendimento (Guia / Paciente)'
                                        name='atendimento'
                                        placeholder='Nº da guia, paciente ou CPF'
                                        propsFormik={propsFormik}
                                        obrigatorio={true}
                                        tamanho={'col-6'}
                                        classeTooltip={'tooltip-input-modal'}
                                        autoFocus={!atendimentoTravado}
                                        isDisabled={atendimentoTravado}
                                        onChange={option => onChangeAtendimento(propsFormik, option)} />
                                    <InputPadrao label='Idade' name='idade' placeholder='Idade' tamanho='col-3' type='number' obrigatorio />
                                    <InputSexo label='Sexo' name='sexo_opcao' placeholder='Sexo' tamanho='col-3' propsFormik={propsFormik} obrigatorio />
                                    <InputPadrao label='Operadora Saúde' name='operadora_saude.label' placeholder='Operadora Saúde' tamanho='col-6' disabled />
                                    <InputPadrao label='Estabelecimento Saúde' name='estabelecimento_saude.label' placeholder='Estabelecimento Saúde' tamanho='col-6' disabled />
                                    <InputPadrao label='Sócio' name='anestesiologista' placeholder='Sócio' tamanho='col-6' disabled />
                                </>}

                                {carregando ? <ContentLoader
                                    speed={2}
                                    width={1200}
                                    height={800}
                                    viewBox="0 0 1200 700"
                                    backgroundColor="#f3f3f3"
                                    foregroundColor="#ecebeb"
                                >
                                    <rect x="20" y="15" rx="4" ry="4" width="350" height="20" />
                                    <rect x="20" y="50" rx="4" ry="4" width="1160" height="200" />
                                </ContentLoader> : <></>}

                                <Formulario propsFormik={propsFormik} />

                                {/* escondido (usuário voltou ao passo 1), o atalho de salvar não pode agir */}
                                {(!guia || guia.visivel) && <Atalho formik={propsFormik} salvando={ocupado} />}
                            </div>
                        </div>
                    </div>
                    <div className='separator mt-40 mb-5'></div>
                    <div className='direita container-botoes mb-3 group-salvar-novo'>
                        <BotaoPadrao texto='Salvar' disabled={ocupado} click={_ => { salvarENovo.current = false }} idTooltip={'salvar'} />
                        {guia && guia.novo && <BotaoPadrao texto='Salvar e Novo' disabled={ocupado} click={_ => { salvarENovo.current = true }} idTooltip={'salvarENovo'} />}
                    </div>
                    <ModalDuasOpcoes
                        titulo="Atenção!"
                        showModal={showModalConfirmarSalvar}
                        setShowModal={setShowModalConfirmarSalvar}
                        funcaoOpcao2={() => Submit(propsFormik.values, propsFormik)}
                        funcaoOpcao1={() => {
                            setSalvando(false)
                            setShowModalConfirmarSalvar(false)
                        }}
                        esconderFechar
                        mensagem={'Você confirma que todas as informações fornecidas estão corretas e deseja finalizar o cadastro da consulta?'}
                        textoOpcao1={'Fechar'}
                        classeOpcao1={'btn-sm btn-primary-modal simples'}
                        textoOpcao2={'Continuar'}
                    />
                </Form>)
            }}
        </Formik >
    )
}

// Campo somente leitura com o visual do InputPadrao, fora do Formik (no passo 2 o cabeçalho vem da guia)
const CampoLeitura = ({ label, valor, tamanho }) => (
    <div className={'form-group ' + tamanho}>
        <label>{label}</label>
        <input className='form-control' value={valor || ''} placeholder={label} disabled readOnly />
    </div>
)

// Largura do cartão conforme a quantidade de opções: até 4 ocupa uma coluna, até 8 duas, acima disso a linha inteira
const classeTamanho = (questao) => {
    const qtdOpcoes = questao.tipo_pergunta === TIPO_PERGUNTA.COMENTARIO ? 0 : (questao.opcoes || []).length
    return qtdOpcoes > 8 ? 'fpos-grande' : qtdOpcoes > 4 ? 'fpos-media' : ''
}

const Formulario = ({ propsFormik }) => {
    return <FieldArray name="agrupamentos">
        {_ => (
            propsFormik.values.agrupamentos && propsFormik.values.agrupamentos.map((agrupamento, aggIdx) => {
                return <div key={agrupamento.id} className='col-12 margin-top-1 mb-20 p-0'>
                    <div className="display-flex mt-20"><h4 className='m-0'><span className={"icon-File-ClipboardFileText"}></span> <span>{agrupamento.descricao}</span></h4></div>

                    <div className='fpos-grid margin-top-1 mb-20'>
                        {
                            agrupamento.questoes?.map((questao, index) => {
                                let algumSelecionado = questao.opcoes ? questao.opcoes.findIndex(x => x.marcado) : -1;
                                let preenchido = algumSelecionado > -1 || questao.observacao?.length > 0;

                                return <div key={questao.id} className={`fpos-questao ${classeTamanho(questao)} ${preenchido ? '' : 'bg-primary-gray'}`}>
                                    <div className='display-flex mb-2 row-space-between'>
                                        <h6 className='mb-0 fs-15'><b>{questao.descricao}</b></h6>
                                        {questao.mostrar_nao_possui ? <InputCheckboxNullable twoOptions={true} disabled={questao.excluido} name={`agrupamentos[${aggIdx}].questoes[${index}].nao_possui`} label={'Negativo'} /> : null}
                                    </div>
                                    <Perguntas prefixName={`agrupamentos[${aggIdx}].`} questao={questao} index={index} propsFormik={propsFormik} aggIdx={aggIdx} disabled={questao.nao_possui} somenteLeitura={questao.excluido} />
                                </div>
                            })
                        }
                    </div>
                </div>
            })
        )}
    </FieldArray >
}

const Cadastro = (props) => {
    const links = [
        () => <Link to={'/dashboard'}>Dashboard</Link>,
        () => <Link className='link-active' to={'/posanestesico/consultaposanestesico/consulta'}>Indicadores Assistenciais</Link>,
    ]
    return (<>
        <HeaderCadastro titulo={"Cadastro de Indicadores Assistenciais"} link={links} />
        <CadastroPosAnestesico {...props} />
    </>
    )
}

export { CadastroPosAnestesico }
export default Cadastro
